import React from 'react';
import { Typography, Paper } from '@mui/material';
import uploadIcon from 'assets/images/UploadIcon';
import { Delete } from '@mui/icons-material';
import { extractFilenameFromAttachmentUrl } from 'utils/fileUtilities';

interface FileUploadProps {
  questionId: string;
  file: File | null;
  onFileChange: (id: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (id: string, event: React.DragEvent<HTMLDivElement>) => void;
  onFileDelete?: (questionId: string, fileUrl: URL) => void;
  isReadOnly?: boolean;
  accept?: string;
  orderItemPlans: URL[];
}

const FileUpload: React.FC<FileUploadProps> = ({
  questionId,
  file,
  onFileChange,
  onDragOver,
  onDrop,
  onFileDelete,
  isReadOnly,
  accept = '.jpg,.jpeg,.png,.pdf',
  orderItemPlans
}) => {
  const allowedFileTypesDisplay = import.meta.env.VITE_APP_ATTACHMENT_ALLOWED_FILE_TYPES_DISPLAY;

  const relevantFiles = orderItemPlans?.filter((url) => extractFilenameFromAttachmentUrl(url).includes(questionId)) || [];

  const handleFileClick = (url: URL) => {
    window.open(url.toString(), '_blank');
  };

  const handleDeleteFile = (url: URL, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onFileDelete) {
      onFileDelete(questionId, url);
    }
  };

  return (
    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
      <input
        id={`file-upload-${questionId}`}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => onFileChange(questionId, e)}
        accept={accept}
        disabled={isReadOnly}
      />
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          width: '50%',
          maxWidth: '300px',
          border: '1px dashed #bdbdbd',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isReadOnly ? 'not-allowed' : 'pointer',
          backgroundColor: isReadOnly ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          '&:hover': {
            backgroundColor: isReadOnly ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.04)'
          }
        }}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(questionId, e)}
      >
        <div
          onClick={(e) => {
            if (isReadOnly) return;
            e.stopPropagation();
            const fileInput = document.getElementById(`file-upload-${questionId}`);
            if (fileInput) fileInput.click();
          }}
          style={{
            textAlign: 'center',
            width: '100%',
            cursor: isReadOnly ? 'not-allowed' : 'pointer'
          }}
        >
          {uploadIcon}
          <Typography
            variant="body2"
            align="center"
            sx={{
              fontSize: '0.85rem',
              color: isReadOnly ? 'text.disabled' : 'text.primary'
            }}
          >
            {isReadOnly ? 'File upload disabled' : 'Click or drag file to this area to upload'}
          </Typography>
          <Typography
            variant="caption"
            align="center"
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              mt: 0.5,
              display: isReadOnly ? 'none' : 'block'
            }}
          >
            Accepted: {allowedFileTypesDisplay}
          </Typography>
          {file && (
            <div style={{ marginTop: '8px', width: '100%' }}>
              <Typography variant="body2" color={isReadOnly ? 'text.secondary' : 'primary'} noWrap sx={{ fontSize: '0.85rem' }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {(file.size / 1024).toFixed(2)} KB
              </Typography>
            </div>
          )}

          {/* Display uploaded files from blob storage */}
          {relevantFiles.length > 0 && (
            <div style={{ marginTop: '16px', width: '100%', borderTop: '1px solid #e0e0e0', paddingTop: '8px' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 500, textAlign: 'center' }}>
                Uploaded Files:
              </Typography>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {relevantFiles.map((url, index) => {
                  const fileName = extractFilenameFromAttachmentUrl(url);
                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        marginBottom: '4px'
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileClick(url);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          color: '#1976d2',
                          fontSize: '0.75rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#1565c0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#1976d2';
                        }}
                      >
                        {fileName}
                      </button>
                      {!isReadOnly && onFileDelete && (
                        <button
                          onClick={(e) => handleDeleteFile(url, e)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#d32f2f',
                            minWidth: '20px',
                            minHeight: '20px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(211, 47, 47, 0.1)';
                            e.currentTarget.style.color = '#b71c1c';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#d32f2f';
                          }}
                          title="Delete file"
                        >
                          <Delete style={{ fontSize: '18px', color: '#d32f2f' }} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default FileUpload;
